import { defineStore } from 'pinia'
import { useTerminologiesClient } from '@/utils/api/api.backend'
import { computed, nextTick, ref, watch } from 'vue'
import { type TerminologyDetailsDto, TenantLanguageDto, TerminologyInstanceDetailsDto } from '@/api/TenantAPIClient'
import { ContentDto, UpdateTerminologyCommand, UpdateTerminologyInstanceCommand, InternationalizedString, ValueDto, TextValueDto, NumberValueDto, DateValueDto, ChoiceValueDto, ContainerValueDto, ElementType, ImportMode, ImportTerminologyInstancesResult, type FileParameter } from '@/api/TenantAPIClient'
import {
  getElementById,
  replaceElement
} from '@/modules/questionnaires/utils/questionnaire-operations'
import { useWorkingCopy } from '@/composables/working-copy'
import { usePagination } from '@/composables/pagination'
import { useSearch } from '@/composables/search'
import { useSort } from '@/composables/sort'
import { downloadBlob } from '@/utils/download'

export type { ImportMode }
export type ImportResult = ImportTerminologyInstancesResult

export const useTerminologyStore = defineStore('terminology', () => {
  const terminologiesClient = useTerminologiesClient()

  const loading = ref(false)
  const publishing = ref<boolean>(false)
  const unpublishing = ref<boolean>(false)
  const saving = ref(false)

  const terminology = ref<TerminologyDetailsDto>()
  const { workingCopy, resetWorkingCopy } = useWorkingCopy(terminology)
  const currentLanguage = ref<TenantLanguageDto>()

  // Instance management
  const { pageNumber, totalCount, pageSize, setPageNumber, setPageSize } = usePagination()
  const { search, debouncedSearch } = useSearch()
  const { sort, apiSort, setSort } = useSort()
  const instances = ref<TerminologyInstanceDetailsDto[]>([])
  const instancesLoading = ref(false)
  const instanceSelection = ref<string[]>([])
  const instanceDeleting = ref(false)
  const instanceCreating = ref(false)
  const instanceSaving = ref(false)

  // Edit instance state
  const editingInstance = ref<TerminologyInstanceDetailsDto | null>(null)
  const editingInstanceWorkingCopy = ref<{
    name: string
    label: InternationalizedString
    values: ValueDto[]
  } | null>(null)

  // Export/Import state
  const exporting = ref(false)
  const importing = ref(false)
  const importResult = ref<ImportResult | null>(null)

  const defaultLanguage = computed(() => {
    return workingCopy.value?.defaultLanguage
  })

  const languages = computed(() => {
    return workingCopy.value?.languages
  })

  const isPublished = computed(() => {
    return terminology.value?.published !== undefined
  })

  const getTerminology = async (id: string) => {
    loading.value = true

    try {
      terminology.value = await terminologiesClient.getTerminology(id)
      await nextTick()
      currentLanguage.value = defaultLanguage.value ?? workingCopy.value?.languages[0]
    } catch (e) {
      console.log(e)
    } finally {
      loading.value = false
    }
  }

  const getElement = (elementId: string) => {
    return getElementById(elementId, workingCopy.value!.contents as ContentDto[])
  }

  const updateElement = (element: ContentDto) => {
    replaceElement(element, workingCopy.value!.contents as ContentDto[])
  }

  const publishTerminology = async () => {
    if (!terminology.value) {
      return
    }

    try {
      publishing.value = true
      await terminologiesClient.publishTerminology(terminology.value.id)
      terminology.value.published = new Date()
    } finally {
      publishing.value = false
    }
  }

  const unpublishTerminology = async () => {
    if (!terminology.value) {
      return
    }

    try {
      unpublishing.value = true
      await terminologiesClient.unpublishTerminology(terminology.value.id)
      terminology.value.published = undefined
    } finally {
      unpublishing.value = false
    }
  }

  const updateTerminology = async () => {
    saving.value = true

    try {
      const command = new UpdateTerminologyCommand({
        name: workingCopy.value!.name,
        terminologyId: workingCopy.value!.id,
        description: workingCopy.value!.description,
        contents: workingCopy.value!.contents as ContentDto[],
        title: workingCopy.value!.title,
        condition: workingCopy.value!.condition,
        languageIds: workingCopy.value?.languages.map((l) => l.id) ?? [],
        defaultLanguageId: workingCopy.value!.defaultLanguage.id,
        sharingOptions: workingCopy.value!.sharingOptions,
        uniqueLabel: workingCopy.value!.uniqueLabel
      })

      await terminologiesClient.updateTerminology(terminology.value!.id, command)
      terminology.value = await terminologiesClient.getTerminology(terminology.value!.id)
      await nextTick()
      currentLanguage.value = defaultLanguage.value ?? workingCopy.value?.languages[0]
    } finally {
      saving.value = false
    }
  }

  // Instance methods
  watch(
    [() => pageNumber.value, () => pageSize.value, () => sort.value, () => debouncedSearch.value],
    async () => {
      if (terminology.value) {
        await getInstances()
      }
    }
  )

  const getInstances = async () => {
    if (!terminology.value) return

    instanceSelection.value = []
    instancesLoading.value = true

    try {
      const { items, totalCount: newTotalCount } = await terminologiesClient.getTerminologyInstances(
        terminology.value.id,
        pageNumber.value,
        pageSize.value,
        apiSort.value,
        search.value,
        undefined, // projectIds
        false // never show hidden/deleted instances
      )

      instances.value = items
      totalCount.value = newTotalCount
    } finally {
      instancesLoading.value = false
    }
  }

  const createInstance = async (name: string) => {
    if (!terminology.value) return

    instanceCreating.value = true

    try {
      await terminologiesClient.createTerminologyInstance(terminology.value.id, name)
      await getInstances()
    } finally {
      instanceCreating.value = false
    }
  }

  const createInstanceWithData = async (data: { name: string; label: InternationalizedString; values: ValueDto[] }) => {
    if (!terminology.value) return

    instanceSaving.value = true

    try {
      // First create the instance with just the name
      const instanceId = await terminologiesClient.createTerminologyInstance(terminology.value.id, data.name)

      // Check if there's anything to update (label translations or non-empty values)
      const hasLabelTranslations = Object.keys(data.label.translations || {}).some(
        key => data.label.translations[key]?.trim()
      )
      const hasValues = data.values.some(v => {
        if ('value' in v && v.value !== undefined && v.value !== null && v.value !== '') return true
        if ('values' in v && Array.isArray(v.values) && v.values.length > 0) return true
        return false
      })

      // Only call update if there's actual data beyond the name
      if (hasLabelTranslations || hasValues) {
        const label = new InternationalizedString()
        label.translations = { ...data.label.translations }

        const command = new UpdateTerminologyInstanceCommand()
        command.terminologyId = terminology.value.id
        command.terminologyInstanceId = instanceId
        command.name = data.name
        command.label = label
        command.values = data.values as ValueDto[]

        await terminologiesClient.updateTerminologyInstance(
          terminology.value.id,
          instanceId,
          command
        )
      }

      await getInstances()
    } finally {
      instanceSaving.value = false
    }
  }

  const hideInstances = async () => {
    if (!instanceSelection.value.length) return

    instanceDeleting.value = true

    try {
      await terminologiesClient.hideTerminologyInstances(instanceSelection.value)
      await getInstances()
    } finally {
      instanceDeleting.value = false
    }
  }

  const deleteInstances = async () => {
    if (!instanceSelection.value.length) return

    instanceDeleting.value = true

    try {
      await terminologiesClient.deleteTerminologyInstances(instanceSelection.value)
      await getInstances()
    } finally {
      instanceDeleting.value = false
    }
  }

  const restoreInstances = async () => {
    if (!instanceSelection.value.length) return

    instanceDeleting.value = true

    try {
      await terminologiesClient.restoreTerminologyInstances(instanceSelection.value)
      await getInstances()
    } finally {
      instanceDeleting.value = false
    }
  }

  const startEditingInstance = (instance: TerminologyInstanceDetailsDto) => {
    editingInstance.value = instance
    // Create a proper InternationalizedString copy
    const labelCopy = new InternationalizedString()
    if (instance.label?.translations) {
      labelCopy.translations = { ...instance.label.translations }
    }

    // Initialize values for each property in the terminology
    const values: ValueDto[] = []
    if (terminology.value?.contents) {
      for (const content of terminology.value.contents) {
        // Find existing value from instance contents if any
        const existingContent = instance.contents?.find(c => c.id === content.id)

        // Create appropriate ValueDto based on element type
        let valueDto: ValueDto | null = null

        switch (content.elementType) {
          case ElementType.TextProperty: {
            const textValue = new TextValueDto()
            textValue.propertyId = content.id
            // Instance content has value as TextValueDto, get the inner value
            textValue.value = (existingContent as any)?.value?.value ?? ''
            valueDto = textValue
            break
          }
          case ElementType.NumberProperty: {
            const numberValue = new NumberValueDto()
            numberValue.propertyId = content.id
            numberValue.value = (existingContent as any)?.value?.value ?? undefined
            valueDto = numberValue
            break
          }
          case ElementType.DateProperty: {
            const dateValue = new DateValueDto()
            dateValue.propertyId = content.id
            dateValue.value = (existingContent as any)?.value?.value ?? undefined
            valueDto = dateValue
            break
          }
          case ElementType.ChoiceProperty: {
            const choiceValue = new ChoiceValueDto()
            choiceValue.propertyId = content.id
            choiceValue.values = (existingContent as any)?.value?.values ?? []
            valueDto = choiceValue
            break
          }
          case ElementType.ContainerProperty: {
            const containerValue = new ContainerValueDto()
            containerValue.propertyId = content.id
            containerValue.values = (existingContent as any)?.value?.values ?? []
            valueDto = containerValue
            break
          }
        }

        if (valueDto) {
          values.push(valueDto)
        }
      }
    }

    editingInstanceWorkingCopy.value = {
      name: instance.name,
      label: labelCopy,
      values
    }
  }

  const cancelEditingInstance = () => {
    editingInstance.value = null
    editingInstanceWorkingCopy.value = null
  }

  // Navigation between instances
  const currentInstanceIndex = computed(() => {
    if (!editingInstance.value) return -1
    return instances.value.findIndex(i => i.id === editingInstance.value!.id)
  })

  const hasPreviousInstance = computed(() => currentInstanceIndex.value > 0)
  const hasNextInstance = computed(() => currentInstanceIndex.value >= 0 && currentInstanceIndex.value < instances.value.length - 1)

  // Save without refreshing the working copy (used for auto-save on navigation)
  const saveInstanceQuiet = async () => {
    if (!terminology.value || !editingInstance.value || !editingInstanceWorkingCopy.value) return

    instanceSaving.value = true

    try {
      const label = new InternationalizedString()
      label.translations = { ...editingInstanceWorkingCopy.value.label.translations }

      const command = new UpdateTerminologyInstanceCommand()
      command.terminologyId = terminology.value.id
      command.terminologyInstanceId = editingInstance.value.id
      command.name = editingInstanceWorkingCopy.value.name
      command.label = label
      command.values = editingInstanceWorkingCopy.value.values as ValueDto[]

      await terminologiesClient.updateTerminologyInstance(
        terminology.value.id,
        editingInstance.value.id,
        command
      )
      await getInstances()
    } finally {
      instanceSaving.value = false
    }
  }

  const editPreviousInstance = async () => {
    if (!hasPreviousInstance.value) return
    // Auto-save current instance before navigating
    await saveInstanceQuiet()
    const prevInstance = instances.value[currentInstanceIndex.value - 1] as TerminologyInstanceDetailsDto
    startEditingInstance(prevInstance)
  }

  const editNextInstance = async () => {
    if (!hasNextInstance.value) return
    // Auto-save current instance before navigating
    await saveInstanceQuiet()
    const nextInstance = instances.value[currentInstanceIndex.value + 1] as TerminologyInstanceDetailsDto
    startEditingInstance(nextInstance)
  }

  const saveInstance = async () => {
    if (!terminology.value || !editingInstance.value || !editingInstanceWorkingCopy.value) return

    instanceSaving.value = true
    const currentInstanceId = editingInstance.value.id

    try {
      // Ensure label is a proper InternationalizedString
      const label = new InternationalizedString()
      label.translations = { ...editingInstanceWorkingCopy.value.label.translations }

      const command = new UpdateTerminologyInstanceCommand()
      command.terminologyId = terminology.value.id
      command.terminologyInstanceId = editingInstance.value.id
      command.name = editingInstanceWorkingCopy.value.name
      command.label = label
      command.values = editingInstanceWorkingCopy.value.values as ValueDto[]

      await terminologiesClient.updateTerminologyInstance(
        terminology.value.id,
        editingInstance.value.id,
        command
      )
      await getInstances()

      // Update editingInstance with fresh data from the refreshed list (keep dialog open)
      const updatedInstance = instances.value.find(i => i.id === currentInstanceId) as TerminologyInstanceDetailsDto | undefined
      if (updatedInstance) {
        editingInstance.value = updatedInstance
        // Refresh working copy with saved data
        startEditingInstance(updatedInstance)
      }
    } finally {
      instanceSaving.value = false
    }
  }

  // Export instances to Excel
  const exportInstances = async (instanceIds?: string[]) => {
    if (!terminology.value) return

    exporting.value = true

    try {
      const response = await terminologiesClient.exportTerminologyInstances(
        terminology.value.id,
        instanceIds?.length ? instanceIds : undefined,
        false
      )

      downloadBlob(response.data, response.fileName ?? `terminology_${terminology.value.id}.xlsx`)
    } finally {
      exporting.value = false
    }
  }

  // Import instances from Excel
  const importInstances = async (file: File, mode: ImportMode = ImportMode.Skip): Promise<ImportResult> => {
    if (!terminology.value) throw new Error('No terminology selected')

    importing.value = true
    importResult.value = null

    try {
      const fileParameter: FileParameter = {
        data: file,
        fileName: file.name
      }

      const result = await terminologiesClient.importTerminologyInstances(
        terminology.value.id,
        mode,
        fileParameter
      )

      importResult.value = result

      // Refresh instances after import
      await getInstances()

      return result
    } finally {
      importing.value = false
    }
  }

  const reset = () => {
    loading.value = false
    saving.value = false
    terminology.value = undefined
    instances.value = []
    editingInstance.value = null
    editingInstanceWorkingCopy.value = null
    importResult.value = null
  }

  return {
    terminology,
    workingCopy,
    loading,
    isPublished,
    publishing,
    unpublishing,
    saving,
    languages,
    defaultLanguage,
    currentLanguage,
    reset,
    resetWorkingCopy,
    updateTerminology,
    getTerminology,
    publishTerminology,
    unpublishTerminology,
    getElement,
    updateElement,
    // Instance exports
    instances,
    instancesLoading,
    instanceSelection,
    instanceDeleting,
    instanceCreating,
    instanceSaving,
    editingInstance,
    editingInstanceWorkingCopy,
    pageNumber,
    pageSize,
    totalCount,
    search,
    sort,
    setPageNumber,
    setPageSize,
    setSort,
    getInstances,
    createInstance,
    createInstanceWithData,
    hideInstances,
    startEditingInstance,
    cancelEditingInstance,
    saveInstance,
    // Instance navigation
    currentInstanceIndex,
    hasPreviousInstance,
    hasNextInstance,
    editPreviousInstance,
    editNextInstance,
    // Export/Import
    exporting,
    importing,
    importResult,
    exportInstances,
    importInstances
  }
})
